import React, { useEffect, useMemo } from 'react';
import { InstantSearch, Configure } from 'react-instantsearch';
import { dealSearch } from '@/utils/typesense/clients/dealSearch';
import { useDispatch, useSelector } from 'react-redux';
import {
	changeRowsPerPage,
	setOrder,
	setOrderBy,
	onOrderChange,
	setSelected,
} from '@/features/table/dealsTableSlice';
import { TableRefresh } from '@/components/table/TableRefresh';
import { joinFilters, DEAL_STATUS_TYPES } from '@/utils/constants';
import {
	CONSENT_STATUS_OPTIONS,
	getConsentStatusFilter,
} from '@/features/informedConsent/helpers';
import { useLocation } from 'react-router-dom';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import InstantSearchDataGrid from '../InstantSearchDataGrid';
import DealTableFilters from './DealTableFilters';
import { parseHeadCells } from '../InstantSearchDataGrid/data';
import {
	useInstantSearchDealFilters,
	useInstantSearchDealOwnerFilters,
} from '@/hooks/useInstantSearchOwnerFilters';
import { getDealHeadCells } from './data';
import { DealBulkActionsToolbar } from '@/components/table/DealBulkActionsToolbar';

const DealTable = ({ pipeline, tab }) => {
	const dispatch = useDispatch();

	const { getFeatureFlags } = useFeatureFlagContext();
	const showNewBusinessKPI = Boolean(getFeatureFlags('newBusinessKPI')?.active);
	const showInformedConsent = Boolean(getFeatureFlags('informedConsent')?.active);

	const isClaims = pipeline == process.env.REACT_APP_PIPELINE_CLAIMS;
	const isEndorsement = pipeline == process.env.REACT_APP_PIPELINE_ENDORSEMENTS;
	const isRenewals = pipeline == process.env.REACT_APP_PIPELINE_RENEWALS;
	const renewalsPipeline = process.env.REACT_APP_PIPELINE_RENEWALS;

	const headCells = useMemo(() => {
		return getDealHeadCells({ pipeline, tab, showNewBusinessKPI, showInformedConsent });
	}, [pipeline, tab, showNewBusinessKPI, showInformedConsent]);

	const { pathname: slug } = useLocation();

	const { rowsPerPage, orders } = useSelector((state) => state.dealTable);

	const orderParams = useMemo(
		() => orders[slug] ?? { order: 'desc', orderBy: 'createdate' },
		[orders, slug],
	);
	const { order, orderBy } = orderParams;

	const { filters, baseFilters } = useInstantSearchDealOwnerFilters();
	const { renewalsFilters, claimsFilters, endorsementsFilters, newBusinessFilters } =
		useInstantSearchDealFilters(pipeline, tab);

	useEffect(() => {
		dispatch(setSelected([]));
	}, [dispatch, pipeline, tab]);

	useEffect(() => {
		const nextOrderBy = !pipeline || pipeline == renewalsPipeline ? 'renewalDate' : orderBy;
		const nextOrder = !pipeline || pipeline == renewalsPipeline ? 'asc' : order;

		if (orders[slug]?.orderBy === nextOrderBy && orders[slug]?.order === nextOrder) {
			return;
		}

		dispatch(
			setOrderBy({
				slug,
				orderBy: nextOrderBy,
			}),
		);
		dispatch(
			setOrder({
				slug,
				order: nextOrder,
			}),
		);
	}, [dispatch, order, orderBy, orders, pipeline, renewalsPipeline, slug]);

	useEffect(() => {
		dispatch(onOrderChange());
	}, [orderBy, order]);

	const handleChangeRowsPerPage = (event) => {
		dispatch(changeRowsPerPage(event.target.value));
	};

	const typesenseFilters = useMemo(() => {
		const hideArchived = `status:!=${DEAL_STATUS_TYPES.archived}`;

		const pipelineFilters = isClaims
			? claimsFilters
			: isRenewals || !pipeline
				? renewalsFilters
				: isEndorsement
					? endorsementsFilters
					: newBusinessFilters;

		const extraDateFilters = [];

		if (filters?.createDate?.length > 0) {
			extraDateFilters.push(...filters.createDate);
		}

		if (filters?.estimatedCloseDate?.length > 0) {
			extraDateFilters.push(...filters.estimatedCloseDate);
		}

		const consentStatuses = showInformedConsent ? (filters?.informedConsentStatus ?? []) : [];
		const consentFilter =
			consentStatuses.length > 0 && consentStatuses.length < CONSENT_STATUS_OPTIONS.length
				? getConsentStatusFilter(consentStatuses)
				: '';

		const filtersArray = [
			...baseFilters,
			...pipelineFilters,
			hideArchived,
			...(extraDateFilters.length > 0 ? extraDateFilters : []),
			...(consentFilter ? [consentFilter] : []),
		];

		return joinFilters(filtersArray);
	}, [
		baseFilters,
		claimsFilters,
		renewalsFilters,
		endorsementsFilters,
		newBusinessFilters,
		filters?.createDate,
		filters?.estimatedCloseDate,
		filters?.informedConsentStatus,
		showInformedConsent,
		isClaims,
		isEndorsement,
		pipeline,
	]);

	const filterSchema = useMemo(() => ({ filters: typesenseFilters }), [typesenseFilters]);

	const columns = useMemo(() => parseHeadCells(headCells), [headCells]);

	return (
		<InstantSearch indexName='deals' searchClient={dealSearch}>
			<Configure {...filterSchema} />
			<TableRefresh table='dealTable' />
			<DealTableFilters {...{ pipeline, tab, showInformedConsent }} />
			<DealBulkActionsToolbar pipeline={pipeline} />
			<InstantSearchDataGrid
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				objectType={'deal'}
				includeTasks={isClaims}
				columns={columns}
				includePolicies={columns.some((c) => c.field === 'policies')}
				includeTransactions={false}
				tab={tab}
				variant={pipeline}
				checkboxSelection
				enableColumnReorder
			/>
		</InstantSearch>
	);
};

DealTable.displayName = 'DealTable';

export default DealTable;
